import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Input, Button, Select, Space, message, Tag, Divider, Alert, Spin, Typography, Table } from 'antd';
import {
  SaveOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
  FacebookOutlined,
  GoogleOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { systemConfigAPI } from '../services/api';

const { Text, Title } = Typography;
const { Option } = Select;

const SERVICE_OPTIONS = [
  { value: 'claude', label: 'Claude (Anthropic)' },
  { value: 'ollama', label: 'OLLAMA (Private Model)' },
  { value: 'openai', label: 'OpenAI' },
];

const MODEL_OPTIONS = {
  CLAUDE_MODEL: [
    { value: 'claude-opus-4-7', label: 'Claude Opus 4.7（最強）' },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6（均衡 ★推薦）' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5（最快/最省）' },
    { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4 (2025-05-14)' },
  ],
  OPENAI_MODEL: [
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini（最省費 ★推薦）' },
    { value: 'gpt-4o', label: 'GPT-4o（高品質）' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
    { value: 'gpt-4.1', label: 'GPT-4.1' },
  ],
  GEMINI_MODEL: [
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (Newest! Recommended)' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (Best Quality)' },
    { value: 'gemini-2.0-flash-001', label: 'Gemini 2.0 Flash 001' },
    { value: 'gemini-2.0-flash-lite-001', label: 'Gemini 2.0 Flash Lite 001' },
  ],
  DEEPSEEK_CHAT_MODEL: [
    { value: "deepseek-chat", label: "DeepSeek Chat (V3) [General Chat]" },
    { value: "deepseek-v3", label: "DeepSeek V3" },
  ],
  DEEPSEEK_CODER_MODEL: [
    { value: "deepseek-coder", label: "DeepSeek Coder [Code]" },
  ],
  AI_MODEL_SELECT: [
    { value: "claude", label: "Claude (Anthropic) [Default]" },
    { value: "deepseek-chat", label: "DeepSeek Chat [Cost-effective]" },
    { value: "deepseek-coder", label: "DeepSeek Coder [Code/Logic]" },
  ],
};

const CONFIG_GROUPS = {
  'Anthropic / Claude': ['ANTHROPIC_API_KEY', 'CLAUDE_MODEL', 'CLAUDE_MAX_TOKENS'],
  'OLLAMA（私有模型）': ['OLLAMA_HOST', 'OLLAMA_MODEL'],
  'AI 引用監測（ChatGPT / Perplexity / Gemini）': ['OPENAI_API_KEY', 'OPENAI_MODEL', 'PERPLEXITY_API_KEY', 'GEMINI_API_KEY', 'GEMINI_MODEL'],
  '服務對應模型': ['SERVICE_CONTENT_GEN', 'SERVICE_CHATBOT', 'SERVICE_PRODUCT_SEARCH'],
  'AlayaEss Dispatch（AI Sales bot 串接 token）': ['DISPATCH_URL', 'DISPATCH_TOKEN', 'DISPATCH_DIRECTORY_TOKEN'],
  'Facebook 行銷': ['FB_APP_ID', 'FB_APP_SECRET', 'FB_ACCESS_TOKEN', 'FB_AD_ACCOUNT_ID', 'FB_PAGE_ID'],
  'Google Ads 行銷': ['GOOGLE_ADS_CLIENT_ID', 'GOOGLE_ADS_CLIENT_SECRET', 'GOOGLE_ADS_DEVELOPER_TOKEN', 'GOOGLE_ADS_REFRESH_TOKEN', 'GOOGLE_ADS_CUSTOMER_ID', 'GOOGLE_ADS_MANAGER_ID'],
  'DeepSeek (AI SEO / AI Research)': ['DEEPSEEK_API_KEY', 'DEEPSEEK_CHAT_MODEL', 'DEEPSEEK_CODER_MODEL'],
  'AI SEO / AI Research - Model Selection': ['AI_MODEL_FOR_SEO', 'AI_MODEL_FOR_RESEARCH'],
};

const GROUP_ICONS = {
  'AI 引用監測（ChatGPT / Perplexity / Gemini）': <BarChartOutlined style={{ color: '#722ed1', marginRight: 8 }} />,
  'Facebook 行銷': <FacebookOutlined style={{ color: '#1877F2', marginRight: 8 }} />,
  'Google Ads 行銷': <GoogleOutlined style={{ color: '#4285F4', marginRight: 8 }} />,
};

const GROUP_ALERTS = {
  'AI 引用監測（ChatGPT / Perplexity / Gemini）': {
    message: 'AI 引用監測所需 API Key',
    description: 'OpenAI Key → ChatGPT 引用查詢（附網路搜尋）；Perplexity Key → Perplexity 查詢；Gemini Key → Google Gemini 查詢（附 Google Search 即時搜尋）。至少設定一個才能執行對應平台監測。',
    type: 'info',
  },
};

const SERVICE_KEYS = new Set(['SERVICE_CONTENT_GEN', 'SERVICE_CHATBOT', 'SERVICE_PRODUCT_SEARCH']);
const TOKEN_KEYS = new Set([
  'ANTHROPIC_API_KEY', 'OPENAI_API_KEY', 'PERPLEXITY_API_KEY', 'GEMINI_API_KEY',
  'FB_APP_SECRET', 'FB_ACCESS_TOKEN',
  'GOOGLE_ADS_CLIENT_SECRET', 'GOOGLE_ADS_DEVELOPER_TOKEN', 'GOOGLE_ADS_REFRESH_TOKEN',
  'DISPATCH_TOKEN', 'DISPATCH_DIRECTORY_TOKEN',
  'DEEPSEEK_API_KEY',
]);

function AIModelConfigPage() {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingClaude, setTestingClaude] = useState(false);
  const [testingOllama, setTestingOllama] = useState(false);
  const [testingOpenai, setTestingOpenai] = useState(false);
  const [testingGemini, setTestingGemini] = useState(false);
  const [testingDeepseek, setTestingDeepseek] = useState(false);
  const [deepseekResult, setDeepseekResult] = useState(null);
  const [claudeResult, setClaudeResult] = useState(null);
  const [ollamaResult, setOllamaResult] = useState(null);
  const [openaiResult, setOpenaiResult] = useState(null);
  const [geminiResult, setGeminiResult] = useState(null);
  const [form] = Form.useForm();

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await systemConfigAPI.list();
      const map = {};
      (res.data || []).forEach((c) => { map[c.key] = c; });
      setConfigs(map);
      // Set form values
      const formValues = {};
      Object.entries(map).forEach(([key, c]) => { formValues[key] = c.value || ''; });
      form.setFieldsValue(formValues);
    } catch (error) {
      message.error('載入設定失敗: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = form.getFieldsValue();
      const configList = Object.entries(values).map(([key, value]) => ({
        key,
        value: value || '',
      }));
      await systemConfigAPI.update({ configs: configList });
      message.success('設定已儲存並套用');
      fetchConfigs();
    } catch (error) {
      message.error('儲存失敗: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestClaude = async () => {
    setTestingClaude(true);
    setClaudeResult(null);
    try {
      const res = await systemConfigAPI.testClaude();
      setClaudeResult(res.data);
    } catch (error) {
      setClaudeResult({ ok: false, error: error.message });
    } finally {
      setTestingClaude(false);
    }
  };

  const handleTestOllama = async () => {
    setTestingOllama(true);
    setOllamaResult(null);
    try {
      const res = await systemConfigAPI.testOllama();
      setOllamaResult(res.data);
    } catch (error) {
      setOllamaResult({ ok: false, error: error.message });
    } finally {
      setTestingOllama(false);
    }
  };

  const handleTestOpenai = async () => {
    setTestingOpenai(true);
    setOpenaiResult(null);
    try {
      const res = await systemConfigAPI.testOpenai();
      setOpenaiResult(res.data);
    } catch (error) {
      setOpenaiResult({ ok: false, error: error.message });
    } finally {
      setTestingOpenai(false);
    }
  };

  const handleTestGemini = async () => {
    setTestingGemini(true);
    setGeminiResult(null);
    try {
      const res = await systemConfigAPI.testGemini();
      setGeminiResult(res.data);
    } catch (error) {
      setGeminiResult({ ok: false, error: error.message });
    } finally {
      setTestingGemini(false);
    }
  };

  const handleTestDeepseek = async () => {
    setTestingDeepseek(true);
    setDeepseekResult(null);
    try {
      const res = await systemConfigAPI.testDeepseek();
      setDeepseekResult(res.data);
    } catch (error) {
      setDeepseekResult({ ok: false, error: error.message });
    } finally {
      setTestingDeepseek(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;
  }

  return (
    <div>
      <Card
        title={<Space><SettingOutlined /> AI 模型管理</Space>}
        extra={
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
            儲存設定
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          {Object.entries(CONFIG_GROUPS).map(([groupName, keys]) => (
            <div key={groupName}>
              <Divider orientation="left">{GROUP_ICONS[groupName] || null}{groupName}</Divider>
              {GROUP_ALERTS[groupName] && (
                <Alert
                  {...GROUP_ALERTS[groupName]}
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
              {keys.map((key) => {
                const config = configs[key];
                if (!config) return null;

                // Service mapping → Select
                if (SERVICE_KEYS.has(key)) {
                  return (
                    <Form.Item key={key} name={key} label={config.description || key}>
                      <Select>
                        {SERVICE_OPTIONS.map((opt) => (
                          <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  );
                }

                // Token fields → Password input
                if (TOKEN_KEYS.has(key)) {
                  return (
                    <Form.Item key={key} name={key} label={config.description || key}>
                      <Input.Password placeholder={`輸入 ${key}`} />
                    </Form.Item>
                  );
                }

                // Model ID keys → Select dropdown
                if (MODEL_OPTIONS[key]) {
                  return (
                    <Form.Item key={key} name={key} label={config.description || key}>
                      <Select showSearch allowClear={false} placeholder="選擇模型">
                        {MODEL_OPTIONS[key].map(opt => (
                          <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  );
                }
                // AI Model selection keys (for SEO / Research)
                if (key === "AI_MODEL_FOR_SEO" || key === "AI_MODEL_FOR_RESEARCH") {
                  return (
                    <Form.Item key={key} name={key} label={config.description || key}>
                      <Select>
                        {MODEL_OPTIONS.AI_MODEL_SELECT.map(opt => (
                          <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  );
                }


                // Regular text fields
                return (
                  <Form.Item key={key} name={key} label={config.description || key}>
                    <Input placeholder={key} />
                  </Form.Item>
                );
              })}
            </div>
          ))}
        </Form>

        <Divider orientation="left">連線測試</Divider>

        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Claude Test */}
          <Card size="small">
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space>
                <ApiOutlined style={{ fontSize: 20 }} />
                <div>
                  <Text strong>Anthropic Claude</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>測試 API Key 是否有效</Text>
                </div>
              </Space>
              <Space>
                {claudeResult && (
                  claudeResult.ok
                    ? <Tag icon={<CheckCircleOutlined />} color="success">連線成功 ({claudeResult.model})</Tag>
                    : <Tag icon={<CloseCircleOutlined />} color="error">{claudeResult.error}</Tag>
                )}
                <Button icon={<ThunderboltOutlined />} onClick={handleTestClaude} loading={testingClaude}>
                  測試
                </Button>
              </Space>
            </Space>
          </Card>

          {/* OLLAMA Test */}
          <Card size="small">
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space>
                <ApiOutlined style={{ fontSize: 20 }} />
                <div>
                  <Text strong>OLLAMA（私有模型）</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>測試連線並列出可用模型</Text>
                </div>
              </Space>
              <Space>
                {ollamaResult && (
                  ollamaResult.ok
                    ? <Tag icon={<CheckCircleOutlined />} color="success">連線成功</Tag>
                    : <Tag icon={<CloseCircleOutlined />} color="error">{ollamaResult.error}</Tag>
                )}
                <Button icon={<ThunderboltOutlined />} onClick={handleTestOllama} loading={testingOllama}>
                  測試
                </Button>
              </Space>
            </Space>

            {ollamaResult && ollamaResult.ok && ollamaResult.models && (
              <div style={{ marginTop: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>可用模型 ({ollamaResult.models.length}):</Text>
                <div style={{ marginTop: 4 }}>
                  {ollamaResult.models.map((m) => (
                    <Tag key={m} style={{ marginBottom: 4 }}>{m}</Tag>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* OpenAI Test */}
          <Card size="small">
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space>
                <ApiOutlined style={{ fontSize: 20 }} />
                <div>
                  <Text strong>OpenAI（AI 引用監測 ChatGPT）</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>測試 OpenAI API Key 是否有效</Text>
                </div>
              </Space>
              <Space>
                {openaiResult && (
                  openaiResult.ok
                    ? <Tag icon={<CheckCircleOutlined />} color="success">連線成功 ({openaiResult.model})</Tag>
                    : <Tag icon={<CloseCircleOutlined />} color="error">{openaiResult.error}</Tag>
                )}
                <Button icon={<ThunderboltOutlined />} onClick={handleTestOpenai} loading={testingOpenai}>
                  測試
                </Button>
              </Space>
            </Space>
          </Card>

          {/* Gemini Test */}
          <Card size="small">
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space>
                <ApiOutlined style={{ fontSize: 20, color: '#4285F4' }} />
                <div>
                  <Text strong>Google Gemini（AI 引用監測 Gemini）</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>測試 Gemini API Key 是否有效（Google AI Studio 取得）</Text>
                </div>
              </Space>
              <Space>
                {geminiResult && (
                  geminiResult.ok
                    ? <Tag icon={<CheckCircleOutlined />} color="success">連線成功 ({geminiResult.model})</Tag>
                    : <Tag icon={<CloseCircleOutlined />} color="error">{geminiResult.error}</Tag>
                )}
                <Button icon={<ThunderboltOutlined />} onClick={handleTestGemini} loading={testingGemini}>
                  測試
                </Button>
              </Space>
            </Space>
          </Card>

          {/* DeepSeek Test */}
          <Card size="small">
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Space>
                <ApiOutlined style={{ fontSize: 20, color: "#4D6BFE" }} />
                <div>
                  <Text strong>DeepSeek (AI SEO / AI Research)</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>Test DeepSeek API Key validity</Text>
                </div>
              </Space>
              <Space>
                {deepseekResult && (
                  deepseekResult.ok
                    ? <Tag icon={<CheckCircleOutlined />} color="success">OK ({deepseekResult.model})</Tag>
                    : <Tag icon={<CloseCircleOutlined />} color="error">{deepseekResult.error}</Tag>
                )}
                <Button icon={<ThunderboltOutlined />} onClick={handleTestDeepseek} loading={testingDeepseek}>
                  Test
                </Button>
              </Space>
            </Space>
          </Card>
        </Space>
      </Card>
    </div>
  );
}

export default AIModelConfigPage;
